import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import { isDropoffStatus, isFinishedStatus } from 'common/utils/status';
import LocationStep from './location-step';
import Collapse from './collapse';
import {
  ViewProofButton,
  AddInstructionActionButton,
  InstructionNote,
} from './location-action';
import { useFeatureFlag } from '../../context/feature-flag';
import styles from './delivery-details.less';

const DeliveryDetails = ({ origin, destinations, status, userType, guid }) => {
  const { hideAddInstruction, hidePhoto } = useFeatureFlag();
  const { isSender, isReceiver } = userType;
  const lastDestination = destinations[destinations.length - 1];
  const intermediateDestinations = destinations.slice(0, -1);

  const renderExtra = useCallback(
    (location, { isPickup } = { isPickup: false }) => {
      const { name, address, instruction, proof } = location;
      const pop = _get(proof, ['pop', 'url', '0']);
      const pod = _get(proof, ['pod', 'url', '0']);

      if (isSender) {
        if (!isPickup) return null;
        if (pop && !hidePhoto)
          return <ViewProofButton image={pop} type="pickup" />;
        if (
          isFinishedStatus(status) ||
          (!instruction && (hideAddInstruction || isDropoffStatus(status)))
        )
          return null;
      }

      if (isReceiver) {
        if (isPickup) return null;
        if (pod && !hidePhoto) return <ViewProofButton image={pod} />;
        if (isFinishedStatus(status) || (!instruction && hideAddInstruction))
          return null;
      }

      if (!instruction)
        return (
          <AddInstructionActionButton
            name={name}
            address={address}
            guid={guid}
          />
        );

      return <InstructionNote text={instruction} />;
    },
    [isSender, isReceiver, status, hideAddInstruction, hidePhoto],
  );

  return (
    <div className={styles.container}>
      {origin && (
        <LocationStep
          type="pickup"
          name={origin.name}
          address={origin.address}
          extra={renderExtra(origin, { isPickup: true })}
        />
      )}

      <Collapse hideThreshold={4}>
        {intermediateDestinations.map((destination, index) => (
          <LocationStep
            key={destination.address}
            name={destination.name}
            address={destination.address}
            level={index + 1}
            extra={renderExtra(destination)}
          />
        ))}
      </Collapse>

      {lastDestination && (
        <LocationStep
          name={lastDestination.name}
          address={lastDestination.address}
          isLast
          level={destinations.length > 1 ? destinations.length : 0}
          extra={renderExtra(lastDestination)}
        />
      )}
    </div>
  );
};

DeliveryDetails.propTypes = {
  origin: PropTypes.shape({
    name: PropTypes.string,
    address: PropTypes.string,
    instruction: PropTypes.string,
    proof: PropTypes.shape({
      pop: PropTypes.shape({ url: PropTypes.arrayOf(PropTypes.string) }),
      pod: PropTypes.shape({ url: PropTypes.arrayOf(PropTypes.string) }),
      isExpired: PropTypes.bool,
    }),
  }).isRequired,
  destinations: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      address: PropTypes.string,
      instruction: PropTypes.string,
      proof: PropTypes.shape({
        pop: PropTypes.shape({ url: PropTypes.arrayOf(PropTypes.string) }),
        pod: PropTypes.shape({ url: PropTypes.arrayOf(PropTypes.string) }),
        isExpired: PropTypes.bool,
      }),
    }),
  ),
  status: PropTypes.string,
  userType: PropTypes.shape({
    isSender: PropTypes.bool,
    isReceiver: PropTypes.bool,
  }),
  guid: PropTypes.string,
};

DeliveryDetails.defaultProps = {
  destinations: [],
  status: '',
  userType: { isSender: false, isReceiver: false },
  guid: '',
};

export default DeliveryDetails;
